import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';

import { useSaveOfferBuilderRoom } from '~/hooks/apis/accommodation/useSaveOfferBuilderRoom';
import { useCreateBatchRoomRates } from '~/hooks/apis/reservation/useCreateBatchRoomRates';
import { useCreateRoomTypeCapacity } from '~/hooks/apis/reservation/useCreateRoomTypeCapacity';

import { reportError } from '~/utils/reportError';

import { useCapacityStep } from './CapacityStep/useCapacityStep';
import ExitConfirmationDialog from './ExitConfirmationDialog';
import { usePackageSetupStep } from './PackageSetupStep/usePackageSetupStep';
import { useOpportunityStep } from './PrerequisitesStep/useOpportunityStep';
import { usePropertyStep } from './PropertyStep/usePropertyStep';
import { useRatePlanStep } from './RatePlanStep/useRatePlanStep';
import { useRoomRatesStep } from './RoomRatesStep/useRoomRatesStep';
import { useRoomsStep } from './RoomsStep/useRoomsStep';
import { useSubmissionStep } from './SubmissionStep/useSubmissionStep';
import { useTaxesFeesStep } from './TaxesFeesStep/useTaxesFeesStep';
import WizardActions from './WizardActions';
import WizardStepContent from './WizardStepContent';
import WizardStepper from './WizardStepper';
import { getActiveStepFlags } from './activeStepFlags';
import { CREATE_NEW_PROPERTY, STEPS } from './constants';
import type { SetUpFlashOfferSectionProps } from './types';
import { usePropertyCreateHandoff } from './usePropertyCreateHandoff';
import { useWizardButtonState } from './useWizardButtonState';
import { useWizardDialog } from './useWizardDialog';
import { useWizardNavigation } from './useWizardNavigation';
import { useWizardStepClearance } from './useWizardStepClearance';

export default function SetUpFlashOfferSection({
  disabled,
  onPropertiesRefresh,
  properties = [],
  vendor,
  vendorId,
}: SetUpFlashOfferSectionProps) {
  const defaultPropertyId = properties.find((property) => !property.deleted_at)?.id ?? CREATE_NEW_PROPERTY;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [propertyWarningMessage, setPropertyWarningMessage] = useState<string | null>(null);
  const [isAdvancingStep, setIsAdvancingStep] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const {
    isPackageSetupStep,
    isPrerequisitesStep,
    isPropertyStep,
    isRatePlanStep,
    isRoomRatesStep,
    isRoomStep,
    isSubmissionStep,
    isTaxesAndFeesStep,
  } = getActiveStepFlags(activeStep);
  const opportunity = useOpportunityStep({ isPrerequisitesStep, vendorId });
  const { trimmedSalesforceOfferId } = opportunity;
  const propertyStep = usePropertyStep({ defaultPropertyId, isPropertyStep, properties });
  const { isCreatePropertyStep, isPropertyStepNextDisabled, selectedProperty, selectedPropertyRoomTypes } =
    propertyStep;
  const saveRoom = useSaveOfferBuilderRoom();
  const roomsStep = useRoomsStep({
    isRoomStep,
    offerId: trimmedSalesforceOfferId,
    saveRoom: saveRoom.mutateAsync,
    selectedProperty,
  });
  const { assetIdByPackageId, isRoomStepNextDisabled, roomDrafts, salesforceRooms, suggestedAssetRoomKeys } = roomsStep;
  const createBatchRoomRates = useCreateBatchRoomRates();
  const createRoomTypeCapacity = useCreateRoomTypeCapacity();
  const ratePlanStep = useRatePlanStep({ isRatePlanStep, vendorId });
  const { createdRatePlans, isRatePlanStepNextDisabled, ratePlanDrafts } = ratePlanStep;
  const roomRatesStep = useRoomRatesStep({
    createBatchRoomRates: createBatchRoomRates.mutateAsync,
    createdRatePlans,
    isRoomRatesStep,
    propertyId: selectedProperty?.id,
    ratePlanDrafts,
    roomDrafts,
    roomTypes: selectedPropertyRoomTypes,
  });
  const { draftRoomRateAssignments, isRoomRatesStepNextDisabled, roomRateRooms } = roomRatesStep;
  const capacityStep = useCapacityStep({
    createRoomTypeCapacity: createRoomTypeCapacity.mutateAsync,
    propertyId: selectedProperty?.id,
  });
  const packageSetupStep = usePackageSetupStep({
    appliedFlashRoomRates: draftRoomRateAssignments,
    assetIdByPackageId,
    isPackageSetupStep,
    propertyId: selectedProperty?.id,
    roomKeyAliases: Object.fromEntries(roomDrafts.flatMap((draft) => (draft.id ? [[draft.draftId, draft.id]] : []))),
    roomTypes: roomRateRooms,
    salesforceRooms,
    setIsAdvancingStep,
    suggestedAssetRoomKeys,
    trimmedSalesforceOfferId,
  });
  const { assetRoomKeys, isPackageSetupStepNextDisabled } = packageSetupStep;
  // Every Salesforce room asset needs a room before Rooms can be left, so a package can't end up with none.
  const hasUnmappedRoomAsset = roomsStep.roomAssetRows.some((roomAsset) => !assetRoomKeys[roomAsset.assetId]);
  const taxesFeesStep = useTaxesFeesStep({ isTaxesAndFeesStep, propertyId: selectedProperty?.id });
  const submissionStep = useSubmissionStep({
    capacityStep,
    packageSetupStep,
    propertyStep,
    ratePlanStep,
    roomRatesStep,
    roomsStep,
    salesforceRooms,
    taxesFeesStep,
    trimmedSalesforceOfferId,
  });

  const stepClearance = useWizardStepClearance({
    activeStep,
    packageSetupStep,
    propertyStep,
    ratePlanStep,
    roomRatesStep,
    roomsStep,
    submissionStep,
    taxesFeesStep,
    trimmedSalesforceOfferId,
  });

  const propertyCreateHandoff = usePropertyCreateHandoff({
    onPropertiesRefresh,
    propertyStep,
    roomsStep,
    setActiveStep,
    setErrorMessage,
    setIsAdvancingStep,
  });

  const { isStepBusy, primaryButtonLabel } = useWizardButtonState({
    activeStep,
    capacityStep,
    isAdvancingStep,
    opportunity,
    propertyStep,
    ratePlanStep,
    roomRatesStep,
    roomsStep,
    submissionStep,
  });

  const wizardNavigation = useWizardNavigation({
    activeStep,
    clearedThrough: stepClearance.clearedThrough,
    isStepBusy,
    opportunity,
    propertyStep,
    ratePlanStep,
    roomsStep,
    setActiveStep,
    setErrorMessage,
    setIsAdvancingStep,
    setPropertyWarningMessage,
    vendor,
    vendorId,
  });

  const wizardDialog = useWizardDialog({
    activeStep,
    capacityStep,
    isStepBusy,
    opportunity,
    packageSetupStep,
    propertyStep,
    ratePlanStep,
    roomRatesStep,
    roomsStep,
    setActiveStep,
    setErrorMessage,
    setIsAdvancingStep,
    setPropertyWarningMessage,
    stepClearance,
    submissionStep,
    taxesFeesStep,
  });
  const { contentPageUrl, goToContentPage, handleOpen } = wizardDialog;

  async function handleSubmitFlashOffer() {
    const result = await submissionStep.submit();

    if (result.success) {
      // Only refetched once the whole submission is confirmed, never mid-attempt -- a later
      // stage's failure would roll the rooms this just fetched back.
      try {
        await onPropertiesRefresh?.(selectedProperty ?? undefined);
      } catch (error) {
        reportError(error);
      }
    }
  }

  return (
    <>
      <Button variant="contained" onClick={handleOpen} disabled={disabled}>
        Set up Flash offer
      </Button>

      <Dialog open={wizardDialog.isDialogOpen} onClose={wizardDialog.handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center' }}>
          Set up Flash offer
          <IconButton
            aria-label="Close"
            disabled={isStepBusy}
            onClick={wizardDialog.handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <WizardStepper
            activeStep={activeStep}
            canGoToStep={stepClearance.canGoToStep}
            clearedThrough={stepClearance.clearedThrough}
            hasSucceeded={submissionStep.hasSucceeded}
            isBusy={isStepBusy}
            onJumpToStep={wizardDialog.handleJumpToStep}
          />

          {propertyWarningMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {propertyWarningMessage}
            </Alert>
          )}

          <WizardStepContent
            activeStep={activeStep}
            errorMessage={errorMessage}
            isStepBusy={isStepBusy}
            onAdvanceStep={() => setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1))}
            onGoToContentPage={goToContentPage}
            onGoToStep={setActiveStep}
            onPropertyCreateError={propertyCreateHandoff.handlePropertyCreateError}
            onPropertyCreateStart={propertyCreateHandoff.handlePropertyCreateStart}
            onPropertyCreateSuccess={propertyCreateHandoff.handlePropertyCreateSuccess}
            onRetrySync={wizardDialog.retryResync}
            onSalesforceIdChange={(value) => {
              opportunity.setSalesforceOfferId(value);
              ratePlanStep.initializeRatePlanDrafts();
              setErrorMessage(null);
            }}
            opportunity={opportunity}
            packageSetupStep={packageSetupStep}
            propertyStep={propertyStep}
            ratePlanStep={ratePlanStep}
            roomRatesStep={roomRatesStep}
            roomsStep={roomsStep}
            setErrorMessage={setErrorMessage}
            submissionStep={submissionStep}
            taxesFeesStep={taxesFeesStep}
            vendorId={vendorId}
          />
        </DialogContent>
        <WizardActions
          activeStep={activeStep}
          contentPageUrl={contentPageUrl}
          hasSubmissionSucceeded={submissionStep.hasSucceeded}
          isCreatePropertyStep={isCreatePropertyStep}
          isPackageSetupStep={isPackageSetupStep}
          isPackageSetupStepNextDisabled={isPackageSetupStepNextDisabled}
          isPrerequisitesStepNextDisabled={opportunity.isPrerequisitesStepNextDisabled}
          isPropertyStepNextDisabled={isPropertyStepNextDisabled}
          isRatePlanStep={isRatePlanStep}
          isRatePlanStepNextDisabled={isRatePlanStepNextDisabled}
          isRoomRatesStepNextDisabled={isRoomRatesStepNextDisabled}
          isRoomStepNextDisabled={isRoomStepNextDisabled || (isRoomStep && hasUnmappedRoomAsset)}
          isStepBusy={isStepBusy}
          isSubmissionStep={isSubmissionStep}
          isSubmissionStepNextDisabled={submissionStep.hasUnresolvedRollbackErrors}
          isTaxesAndFeesStep={isTaxesAndFeesStep}
          onGoToContentPage={goToContentPage}
          onNext={wizardNavigation.handleNext}
          onPrevious={wizardDialog.handlePrevious}
          onSubmit={handleSubmitFlashOffer}
          primaryButtonLabel={primaryButtonLabel}
          trimmedSalesforceOfferId={trimmedSalesforceOfferId}
        />
      </Dialog>

      <ExitConfirmationDialog
        onCancel={wizardDialog.handleCancelClose}
        onConfirm={wizardDialog.handleConfirmClose}
        open={wizardDialog.isExitConfirmationOpen}
      />
    </>
  );
}
